'use client';

import { useState } from 'react';
import HeaderMenuImage from '@/components/sections/builder/elements/header/header/header-menu-image';
import {
  HeaderElementDataType,
  HeaderMenuElementDataType,
} from '@/components/sections/builder/elements/header/header/type';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { IMenuItem } from '@/types/navigation';
import { ChevronDownIcon } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

export default function HeaderMenuContent({
  menu,
  isMegaMenu,
  parentData,
  preview,
}: {
  menu: IMenuItem;
  isMegaMenu: boolean;
  parentData?: HeaderElementDataType;
  preview?: boolean;
}) {
  const query = useSearchParams();
  const themeId: string = query.get('theme_id') || '';
  const token = query.get('token') || '';

  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);

  const data = {
    name: menu.title,
    label: menu.title,
    link: menu.url_path,
    subMenuType: (() => {
      // const hasSubChild = menu.children.some((item) => item?.children?.length > 0);

      if (!menu.children?.length) {
        return 'NONE';
      } else if (isMegaMenu) {
        return 'MEGA_MENU';
      } else {
        return 'DROPDOWN';
      }
    })(),

    dropdownWidth: 'AUTO',
    megaMenuWidth: 'FULL_WIDTH',
    subMenus: [],
  } as HeaderMenuElementDataType;

  return (
    <>
      {/* Mobile components */}
      <Accordion type='single' collapsible className='block w-full lg:hidden'>
        <AccordionItem value='item-1' className='w-full'>
          <AccordionTrigger
            className={cn(
              'w-full flex-row-reverse justify-between text-base  font-normal [&>svg]:h-5 [&>svg]:w-5',
              data.subMenuType === 'NONE' && '[&>svg]:opacity-0'
            )}
          >
            {data.subMenuType === 'NONE' ? (
              <a
                href={
                  data.link?.includes('http')
                    ? data.link
                    : `/${data.link}${
                        token
                          ? `?theme_id=${themeId}&token=${token}`
                          : preview
                          ? `?theme_id=${themeId}&isPreview=true`
                          : ''
                      }`
                }
              >
                {data.label}
              </a>
            ) : (
              data.label
            )}
          </AccordionTrigger>
          <AccordionContent className='text-left'>
            {data.subMenuType === 'DROPDOWN' && (
              <ul className='py-2 text-base text-gray-700 dark:text-gray-400'>
                {menu.children?.map((subMenu) => (
                  <li key={subMenu.id}>
                    <a
                      href={
                        subMenu.url_path.includes('http')
                          ? subMenu.url_path
                          : `/${subMenu.url_path}${
                              token
                                ? `?theme_id=${themeId}&token=${token}`
                                : preview
                                ? `?theme_id=${themeId}&isPreview=true`
                                : ''
                            }`
                      }
                      className='block px-4 py-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
                    >
                      {subMenu.title}
                    </a>
                    {subMenu.children?.length > 0 && (
                      <button
                        onClick={() =>
                          setOpenSubMenu(openSubMenu === subMenu.id ? null : subMenu.id)
                        }
                      ></button>
                    )}
                    {openSubMenu === subMenu.id && subMenu.children?.length > 0 && (
                      <ul className='pl-4'>
                        {subMenu.children.map((child) => (
                          <li key={child.id}>
                            <a
                              href={
                                child.url_path.includes('http')
                                  ? child.url_path
                                  : `/${child.url_path}${
                                      token
                                        ? `?theme_id=${themeId}&token=${token}`
                                        : preview
                                        ? `?theme_id=${themeId}&isPreview=true`
                                        : ''
                                    }`
                              }
                              className='block px-4 py-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
                            >
                              {child.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {data.subMenuType === 'MEGA_MENU' && (
              <div className='flex flex-col justify-between w-full gap-4 px-3 py-2'>
                {menu.children?.map((menuItem) => (
                  <div key={menuItem.id}>
                    {menuItem.title && (
                      <a
                        href={
                          menuItem.url_path.includes('http')
                            ? menuItem.url_path
                            : `/${menuItem.url_path}${
                                token
                                  ? `?theme_id=${themeId}&token=${token}`
                                  : preview
                                  ? `?theme_id=${themeId}&isPreview=true`
                                  : ''
                              }`
                        }
                        className={cn(
                          'mb-1 text-base',
                          menuItem.children.length
                            ? 'font-semibold text-black'
                            : 'rounded-lg p-2 text-sm font-normal text-[#52535B] hover:bg-gray-50 dark:hover:bg-gray-700'
                        )}
                      >
                        {menuItem.title}
                      </a>
                    )}
                    <ul>
                      {menuItem.children?.map((subMenu) => (
                        <li key={subMenu.id}>
                          <a
                            href={
                              subMenu.url_path?.includes('http')
                                ? subMenu.url_path
                                : `/${subMenu.url_path}${
                                    token
                                      ? `?theme_id=${themeId}&token=${token}`
                                      : preview
                                      ? `?theme_id=${themeId}&isPreview=true`
                                      : ''
                                  }`
                            }
                            className='flex p-3 rounded-lg gap-x-2 hover:bg-gray-50 dark:hover:bg-gray-700'
                          >
                            <div>
                              <div>{subMenu.title}</div>
                            </div>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className='grid grid-cols-1 gap-2'>
                  {menu.images?.slice(0, 3).map((image, index) => (
                    <div key={index}>
                      <HeaderMenuImage
                        title={image.title}
                        src={image.image_url}
                        parentData={parentData}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Desktop components */}
      <Popover>
        <PopoverTrigger
          className={cn(
            'hidden flex-shrink-0 items-center gap-x-2 text-base lg:flex',
            menu.is_highlight && 'rounded bg-red-500 px-2 py-1 text-white'
          )}
        >
          {data.subMenuType === 'NONE' ? (
            <Link
              className='flex-shrink-0'
              href={
                data.link?.includes('http')
                  ? data.link
                  : `/${data.link}${
                      token
                        ? `?theme_id=${themeId}&token=${token}`
                        : preview
                        ? `?theme_id=${themeId}&isPreview=true`
                        : ''
                    }`
              }
            >
              {data.label}
            </Link>
          ) : (
            <span>{data.label}</span>
          )}
          {data.subMenuType !== 'NONE' && <ChevronDownIcon className='w-4 h-4' />}
        </PopoverTrigger>
        {data.subMenuType === 'DROPDOWN' && (
          <PopoverContent
            className={cn(
              'theme_dropdown_popup_border theme_font theme_dropdown_popup_shadow p-2',
              parentData?.colorSchemeMenuHeader?.background
                ? 'scheme-color-menu-header'
                : 'bg-popover',
              data.dropdownWidth === 'AUTO' && 'w-auto',
              data.dropdownWidth === 'MEDIUM' && 'w-80',
              data.dropdownWidth === 'LARGE' && 'w-96'
            )}
            sideOffset={10}
            align='start'
          >
            {data.subMenuType === 'DROPDOWN' && (
              <ul className='py-2 text-base'>
                {menu.children?.map((subMenu) => (
                  <li key={subMenu.id}>
                    <div className='flex items-center justify-between'>
                      <Link
                        href={
                          subMenu.url_path?.includes('http')
                            ? subMenu.url_path
                            : `/${subMenu.url_path}${
                                token
                                  ? `?theme_id=${themeId}&token=${token}`
                                  : preview
                                  ? `?theme_id=${themeId}&isPreview=true`
                                  : ''
                              }`
                        }
                        className='block px-4 py-2 text-sm rounded-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
                      >
                        <span className='text-sm'>{subMenu.title}</span>
                      </Link>
                      {subMenu.children.length > 0 && (
                        <button
                          onClick={() =>
                            setOpenSubMenu(openSubMenu === subMenu.id ? null : subMenu.id)
                          }
                          className='focus:outline-none'
                        >
                          <ChevronDownIcon
                            className={`w-4 transition-transform ${
                              openSubMenu === subMenu.id ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                      )}
                    </div>
                    {openSubMenu === subMenu.id && (
                      <ul className='pl-1'>
                        {subMenu.children.map((child) => (
                          <li key={child.id}>
                            <Link
                              href={
                                child.url_path.includes('http')
                                  ? child.url_path
                                  : `/${child.url_path}${
                                      token
                                        ? `?theme_id=${themeId}&token=${token}`
                                        : preview
                                        ? `?theme_id=${themeId}&isPreview=true`
                                        : ''
                                    }`
                              }
                              className='block px-4 py-2 text-sm rounded-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
                            >
                              {child.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </PopoverContent>
        )}

        {data.subMenuType === 'MEGA_MENU' && (
          <PopoverContent
            sideOffset={10}
            className={cn(
              'theme_font flex justify-between px-10',
              parentData?.colorSchemeMenuHeader?.background
                ? 'scheme-color-menu-header'
                : 'bg-popover',
              data.megaMenuWidth === 'SMALL' && 'max-w-lg',
              data.megaMenuWidth === 'MEDIUM' && 'max-w-4xl',
              data.megaMenuWidth === 'FULL_WIDTH' && 'popover_full_width rounded-none'
            )}
          >
            <div className='mr-10'>
              <div
                className={cn(
                  menu.children?.some(
                    (menuItem) => menuItem.children && menuItem.children.length > 0
                  )
                    ? 'mega_menu_grid_col grid gap-5'
                    : 'flex flex-col gap-3'
                )}
              >
                {menu.children?.map((menuItem) => (
                  <div key={menuItem?.id}>
                    {!!menuItem.title && (
                      <Link
                        href={
                          menuItem.url_path.includes('http')
                            ? menuItem.url_path
                            : `/${menuItem.url_path}${
                                token
                                  ? `?theme_id=${themeId}&token=${token}`
                                  : preview
                                  ? `?theme_id=${themeId}&isPreview=true`
                                  : ''
                              }`
                        }
                        className={cn(
                          'mb-1 text-base hover:bg-black/5',
                          menuItem.children.length ? 'font-semibold' : 'rounded-lg p-2 text-xs'
                        )}
                      >
                        {menuItem.title}
                      </Link>
                    )}
                    <ul className='mt-3'>
                      {menuItem?.children?.map((subMenu) => (
                        <li key={subMenu.id}>
                          <Link
                            href={
                              subMenu.url_path?.includes('http')
                                ? subMenu.url_path
                                : `/${subMenu.url_path}${
                                    token
                                      ? `?theme_id=${themeId}&token=${token}`
                                      : preview
                                      ? `?theme_id=${themeId}&isPreview=true`
                                      : ''
                                  }`
                            }
                            className={cn(
                              'flex gap-x-2 rounded-lg p-2 dark:hover:bg-gray-700',
                              parentData?.colorSchemeMenuHeader?.btnSolidlabel
                                ? 'hover-color-scheme'
                                : 'hover:bg-gray-50'
                            )}
                          >
                            <div>
                              <div>{subMenu.title}</div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className='px-8'>
              <div className='flex gap-2'>
                {menu.images?.slice(0, 3).map((image, index) => (
                  <Link
                    href={
                      image.url_path?.includes('http')
                        ? image.url_path
                        : `/${image.url_path}${
                            token
                              ? `?theme_id=${themeId}&token=${token}`
                              : preview
                              ? `?theme_id=${themeId}&isPreview=true`
                              : ''
                          }`
                    }
                    key={index}
                    passHref={image.url_path?.includes('http')}
                  >
                    <HeaderMenuImage
                      title={image.title}
                      src={image.image_url}
                      parentData={parentData}
                    />
                  </Link>
                ))}
              </div>
            </div>
          </PopoverContent>
        )}
      </Popover>
      <style jsx global>
        {`
          .popover_full_width {
            width: calc(var(--radix-popover-content-available-width) + 1px);
          }
          .hover-color-scheme:hover {
            background-color: ${parentData?.colorSchemeMenuHeader?.btnSolidlabel};
          }
          .scheme-color-menu-header {
            background-color: ${parentData?.colorSchemeMenuHeader?.background};
            color: ${parentData?.colorSchemeMenuHeader?.text};
          }
          .mega_menu_grid_col {
            grid-template-columns: repeat(5, minmax(0, 1fr));
          }
        `}
      </style>
    </>
  );
}
